import React, { useState, useEffect } from 'react';
import BBALoader from '../../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import SaveButton from '../../components/SaveButton';
import { 
  getPillarsSettings, 
  updatePillarsSettings,
  getPillarDisplayName, 
  getPillarIcon 
} from '../../services/pillarsService';
import { useAuth } from '../../contexts/AuthContext';
import { getInternalUsers } from '../../services/userService';
import PortalBackground from '../../components/PortalBackground';

const RoutingTemplate = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State
  const [pillarsSettings, setPillarsSettings] = useState({});
  const [pillarsLoading, setPillarsLoading] = useState(true);
  const [internalUsers, setInternalUsers] = useState([]);
  const [error, setError] = useState(null);
  const [pillarsChanged, setPillarsChanged] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchPillarsSettings();
      fetchInternalUsers();
    }
  }, [currentUser]);

  const fetchPillarsSettings = async () => {
    try {
      setPillarsLoading(true);
      const settings = await getPillarsSettings(currentUser.uid);
      console.log('Fetched pillars settings:', settings);
      setPillarsSettings(settings);
    } catch (error) {
      console.error('Error fetching pillars settings:', error);
      setError('Failed to fetch pillars settings');
    } finally {
      setPillarsLoading(false);
    }
  };

  const fetchInternalUsers = async () => {
    try {
      const users = await getInternalUsers();
      setInternalUsers(users);
    } catch (error) {
      console.error('Error fetching internal users:', error);
      setError('Failed to fetch internal users');
      setInternalUsers([]);
    }
  };

  const getInternalUserName = (userId) => {
    const user = internalUsers.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  const handlePillarSettingChange = (pillar, field, value) => {
    console.log('Pillar setting change:', { pillar, field, value });
    setPillarsSettings(prev => {
      const newSettings = {
        ...prev,
        [pillar]: {
          ...(prev[pillar] || {}),
          [field]: value
        }
      };
      console.log('New pillars settings:', newSettings);
      return newSettings;
    });
    setPillarsChanged(true);
    console.log('pillarsChanged set to true');
  };

  const handleSave = async () => {
    if (pillarsChanged && currentUser) {
      try {
        console.log('Saving pillars settings:', pillarsSettings);
        await updatePillarsSettings(currentUser.uid, pillarsSettings);
        setPillarsChanged(false);
        setError(null);
        console.log('Pillars settings saved successfully');
        
        // Refresh the settings to show the updated values
        await fetchPillarsSettings();
      } catch (error) {
        console.error('Error saving pillars settings:', error);
        setError('Failed to save pillars settings');
      }
    }
  };

  const renderPillars = () => {
    const pillars = ['sales', 'operations', 'finance', 'technology', 'marketing', 'hr'];

         if (pillarsLoading) {
       return (
         <div className="space-y-6">
           <div className="p-6">
             <div className="flex items-center justify-center h-32">
               <BBALoader size={32} />
             </div>
           </div>
         </div>
       );
     }

         return (
       <div className="space-y-6">
         <div className="p-6">
           <div className="space-y-6">
                         {pillars.map((pillar) => (
               <div key={pillar} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
                 <div className="flex items-center mb-4">
                   <span className="text-2xl mr-3">{getPillarIcon(pillar)}</span>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                     {getPillarDisplayName(pillar)}
                   </h3>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Schedule Link
                     </label>
                     <input
                       type="url"
                       value={pillarsSettings[pillar]?.scheduleLink || ''}
                       onChange={(e) => handlePillarSettingChange(pillar, 'scheduleLink', e.target.value)}
                       className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                       placeholder="https://calendly.com/example/pillar-1on1"
                     />
                     <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                       Link for scheduling calls with {getPillarDisplayName(pillar)} team
                     </p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Question Assignee
                     </label>
                     <select
                       value={pillarsSettings[pillar]?.questionAssignee || ''}
                       onChange={(e) => handlePillarSettingChange(pillar, 'questionAssignee', e.target.value)}
                       className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                     >
                       <option value="">Select an internal user</option>
                       {internalUsers.length === 0 ? (
                         <option value="" disabled>No internal users found</option>
                       ) : (
                         internalUsers.map((user) => (
                           <option key={user.id} value={user.id}>
                             {user.name}
                           </option>
                         ))
                       )}
                     </select>
                     <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                       Internal user to assign questions about {getPillarDisplayName(pillar)}
                     </p>
                   </div>
                 </div>
                {/* Manage Assignees button removed */}
               </div>
             ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/templates')} />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">Routing Template</h1>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">Changed: {pillarsChanged ? 'Yes' : 'No'}</span>
            <SaveButton onClick={handleSave} disabled={!pillarsChanged} />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
            {error}
          </div>
        )}

        {/* Content */}
        {renderPillars()}
      </div>
    </div>
  );
};

export default RoutingTemplate;
